// src/components/routing/ZoneOperationsMap.js
// --- UPDATE (Field Ops · Zones v1) — NEW FILE ---------------------------------------------
// Live operations map: every zone / constituency in its own colour (fill strength = risk),
// open work orders (colour = priority, dark ring = overdue, hollow = unassigned) and field staff
// (initials; green = online, grey = offline). Click a zone to select it, a job or a person for
// details. Layer toggles, legend, "fit all" and a focus on the selected zone.
// Performance: canvas renderer (preferCanvas), memoised layers, cached technician icons,
// at most MAX_JOB_MARKERS job markers (urgent first).
// --- END UPDATE ---------------------------------------------------------------------------
import React, { memo, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Box, HStack, Wrap, WrapItem, Text, Switch, FormLabel, Button, Icon, Badge, useColorModeValue } from '@chakra-ui/react';
import { MdCenterFocusStrong, MdZoomOutMap } from 'react-icons/md';
import { MapContainer, TileLayer, GeoJSON, CircleMarker, Marker, Tooltip, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { PRIORITY_COLORS, zoneLabel, dash, ago } from './zoneGeo';

const NASSAU = [25.06, -77.345];
const MAX_JOB_MARKERS = 1500;
const PRIORITY_RANK = { critical: 0, high: 1, medium: 2, low: 3 };

const iconCache = new Map();
const techIcon = (initials, online, selected) => {
    const key = `${initials}|${online}|${selected}`;
    if (!iconCache.has(key)) {
        const bg = online ? '#16a34a' : '#718096';
        iconCache.set(key, L.divIcon({
            className: 'fs-tech-icon',
            html: `<div style="width:26px;height:26px;border-radius:50%;background:${bg};color:#fff;font:700 10px/26px system-ui,sans-serif;text-align:center;border:2px solid ${selected ? '#facc15' : '#fff'};box-shadow:0 1px 4px rgba(0,0,0,.4)">${initials}</div>`,
            iconSize: [26, 26], iconAnchor: [13, 13],
        }));
    }
    return iconCache.get(key);
};
const initialsOf = (name) => String(name || '?').split(/\s+/).filter(Boolean).slice(0, 2).map((p) => p[0].toUpperCase().replace(/[^A-Z0-9]/g, '')).join('') || '?'; // safe for divIcon html

function FitBounds({ zones, workOrders, technicians, focusZone, fitKey }) {
    const map = useMap();
    const done = useRef(null);
    useEffect(() => {
        if (focusZone?.bbox) {
            const [x1, y1, x2, y2] = focusZone.bbox;
            map.fitBounds([[y1, x1], [y2, x2]], { padding: [30, 30], maxZoom: 15 });
            return;
        }
        if (done.current === fitKey) return;
        const pts = [];
        zones.forEach((z) => { if (z.bbox) { pts.push([z.bbox[1], z.bbox[0]], [z.bbox[3], z.bbox[2]]); } });
        if (!pts.length) {
            workOrders.forEach((w) => w.lat !== null && pts.push([w.lat, w.lng]));
            technicians.forEach((t) => t.lat !== null && pts.push([t.lat, t.lng]));
        }
        if (pts.length) { map.fitBounds(pts, { padding: [24, 24], maxZoom: 14 }); done.current = fitKey; }
    }, [map, zones, workOrders, technicians, focusZone, fitKey]);
    return null;
}

const ZoneLayer = memo(({ zone, selected, dimmed, onSelect }) => {
    const style = useMemo(() => ({
        color: zone.color, weight: selected ? 4 : 2, opacity: dimmed ? 0.35 : 0.95,
        fillColor: zone.color, fillOpacity: dimmed ? 0.05 : 0.12 + Math.min(zone.risk?.score || 0, 100) / 100 * 0.33,
        dashArray: zone.stats?.techs_online === 0 && zone.stats?.open > 0 ? '6 4' : null,
    }), [zone.color, zone.risk?.score, zone.stats?.techs_online, zone.stats?.open, selected, dimmed]);
    const handlers = useMemo(() => ({ click: () => onSelect?.(zone.id) }), [onSelect, zone.id]);
    if (!zone.geometry) return null;
    return (
        <GeoJSON key={`${zone.id}-${zone.color}-${selected}-${dimmed}`} data={zone.geometry} style={style} eventHandlers={handlers}>
            <Tooltip sticky>
                <div style={{ fontWeight: 700 }}>{zoneLabel(zone)}</div>
                <div>{zone.stats.open} open · {zone.stats.critical} critical · {zone.stats.techs_online} online</div>
                <div>Risk {zone.risk.score}/100</div>
            </Tooltip>
        </GeoJSON>
    );
});
ZoneLayer.displayName = 'ZoneLayer';
ZoneLayer.propTypes = { zone: PropTypes.object.isRequired, selected: PropTypes.bool, dimmed: PropTypes.bool, onSelect: PropTypes.func };

const JobMarkers = memo(({ jobs, zoneById, onOpenJob }) => jobs.map((w) => (
    <CircleMarker key={`wo-${w.id}`} center={[w.lat, w.lng]} radius={w.priority === 'critical' ? 8 : w.priority === 'high' ? 7 : 5}
        pathOptions={{ color: w.overdue ? '#1a202c' : '#ffffff', weight: w.overdue ? 3 : 1.5, fillColor: PRIORITY_COLORS[w.priority] || '#718096', fillOpacity: w.assigned_to ? 0.95 : 0.45 }}
        eventHandlers={{ click: () => onOpenJob?.(w) }}>
        <Popup>
            <div style={{ minWidth: 180 }}>
                <div style={{ fontWeight: 700 }}>{w.title}</div>
                <div>#{w.id} · {w.priority}{w.overdue ? ' · OVERDUE' : ''}</div>
                <div>Status: {dash(w.status)}</div>
                <div>Zone: {zoneLabel(zoneById.get(String(w.zone_id))) || 'outside every zone'}</div>
                <div>Assigned: {w.assignee_name || (w.assigned_to ? w.assigned_to : 'unassigned')}</div>
                {w.due_at && <div>Due: {new Date(w.due_at).toLocaleString()}</div>}
            </div>
        </Popup>
    </CircleMarker>
)));
JobMarkers.displayName = 'JobMarkers';

const TechMarkers = memo(({ techs, zoneById, selectedTechId, onOpenTech }) => techs.map((t) => (
    <Marker key={`tech-${t.id}`} position={[t.lat, t.lng]} icon={techIcon(initialsOf(t.name), t.online, String(selectedTechId) === String(t.id))}
        eventHandlers={{ click: () => onOpenTech?.(t) }} zIndexOffset={t.online ? 500 : 0}>
        <Tooltip direction="top" offset={[0, -12]}>
            <div style={{ fontWeight: 700 }}>{t.name}</div>
            <div>{t.online ? 'Online' : 'Offline'} · seen {ago(t.last_seen)}</div>
            <div>{zoneLabel(zoneById.get(String(t.zone_id))) === '—' ? 'Outside every zone' : zoneLabel(zoneById.get(String(t.zone_id)))} · {t.open_jobs} open job{t.open_jobs === 1 ? '' : 's'}</div>
        </Tooltip>
    </Marker>
)));
TechMarkers.displayName = 'TechMarkers';

function ZoneOperationsMap({ data, selectedZoneId, onSelectZone, onOpenJob, onOpenTech, selectedTechId, height = '520px', showUnzonedOnly = false }) {
    const [layers, setLayers] = useState({ zones: true, jobs: true, techs: true, offline: true });
    const [fitKey, setFitKey] = useState(0);
    const border = useColorModeValue('gray.200', 'gray.700');
    const panel = useColorModeValue('whiteAlpha.900', 'blackAlpha.700');
    const muted = useColorModeValue('gray.600', 'gray.300');

    const zones = data?.zones || [];
    const zoneById = useMemo(() => new Map(zones.map((z) => [String(z.id), z])), [zones]);
    const selected = selectedZoneId != null ? zoneById.get(String(selectedZoneId)) : null;
    const jobs = useMemo(() => {
        let l = (data?.work_orders || []).filter((w) => w.lat !== null && w.lng !== null);
        if (showUnzonedOnly) l = l.filter((w) => w.zone_id === null);
        else if (selected) l = l.filter((w) => String(w.zone_id) === String(selected.id));
        return l.sort((a, b) => (PRIORITY_RANK[a.priority] ?? 9) - (PRIORITY_RANK[b.priority] ?? 9)).slice(0, MAX_JOB_MARKERS);
    }, [data?.work_orders, selected, showUnzonedOnly]);
    const techs = useMemo(() => (data?.technicians || []).filter((t) => t.lat !== null && (layers.offline || t.online)), [data?.technicians, layers.offline]);
    const hiddenJobs = (data?.work_orders || []).filter((w) => w.lat !== null).length - jobs.length;

    const toggle = (k) => setLayers((s) => ({ ...s, [k]: !s[k] }));
    return (
        <Box position="relative" h={height} borderRadius="xl" overflow="hidden" borderWidth="1px" borderColor={border} data-testid="zone-ops-map">
            <MapContainer center={NASSAU} zoom={12} preferCanvas style={{ height: '100%', width: '100%' }} scrollWheelZoom>
                <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors' />
                {layers.zones && zones.map((z) => (
                    <ZoneLayer key={z.id} zone={z} selected={String(selectedZoneId) === String(z.id)} dimmed={!!selected && String(selected.id) !== String(z.id)} onSelect={onSelectZone} />
                ))}
                {layers.jobs && <JobMarkers jobs={jobs} zoneById={zoneById} onOpenJob={onOpenJob} />}
                {layers.techs && <TechMarkers techs={techs} zoneById={zoneById} selectedTechId={selectedTechId} onOpenTech={onOpenTech} />}
                <FitBounds zones={zones} workOrders={data?.work_orders || []} technicians={data?.technicians || []} focusZone={selected} fitKey={`${zones.length}-${fitKey}`} />
            </MapContainer>

            <Box position="absolute" top={2} left={12} zIndex={500} bg={panel} borderRadius="lg" px={3} py={2} boxShadow="md">
                <Wrap spacing={3} align="center">
                    {[['zones', 'Zones'], ['jobs', 'Work orders'], ['techs', 'Field staff'], ['offline', 'Offline staff']].map(([k, label]) => (
                        <WrapItem key={k}>
                            <HStack spacing={1}>
                                <Switch size="sm" id={`layer-${k}`} isChecked={layers[k]} onChange={() => toggle(k)} colorScheme="blue" />
                                <FormLabel htmlFor={`layer-${k}`} mb={0} fontSize="xs" fontWeight="600">{label}</FormLabel>
                            </HStack>
                        </WrapItem>
                    ))}
                    <WrapItem>
                        <Button size="xs" variant="ghost" leftIcon={<Icon as={selected ? MdCenterFocusStrong : MdZoomOutMap} />} onClick={() => { onSelectZone?.(null); setFitKey((k) => k + 1); }}>
                            {selected ? 'Show all zones' : 'Fit all'}
                        </Button>
                    </WrapItem>
                </Wrap>
            </Box>

            <Box position="absolute" bottom={6} left={2} zIndex={500} bg={panel} borderRadius="lg" px={3} py={2} boxShadow="md" maxW="300px">
                <Wrap spacing={2} mb={1}>
                    {Object.entries(PRIORITY_COLORS).map(([p, c]) => (
                        <WrapItem key={p}><HStack spacing={1}><Box w="10px" h="10px" borderRadius="full" bg={c} /><Text fontSize="2xs" textTransform="capitalize">{p}</Text></HStack></WrapItem>
                    ))}
                    <WrapItem><HStack spacing={1}><Box w="10px" h="10px" borderRadius="full" borderWidth="2px" borderColor="gray.800" /><Text fontSize="2xs">Overdue</Text></HStack></WrapItem>
                    <WrapItem><HStack spacing={1}><Box w="10px" h="10px" borderRadius="full" bg="green.500" /><Text fontSize="2xs">Staff online</Text></HStack></WrapItem>
                </Wrap>
                <Text fontSize="2xs" color={muted}>Zone fill = risk · dashed border = open work, nobody online{hiddenJobs > 0 && !selected && !showUnzonedOnly ? ` · ${hiddenJobs} low-priority markers hidden` : ''}</Text>
            </Box>

            {selected && (
                <Badge position="absolute" top={2} right={2} zIndex={500} px={3} py={1} borderRadius="full" bg={selected.color} color="white" boxShadow="md">
                    {zoneLabel(selected)}
                </Badge>
            )}
        </Box>
    );
}

ZoneOperationsMap.propTypes = {
    data: PropTypes.object, selectedZoneId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]), onSelectZone: PropTypes.func,
    onOpenJob: PropTypes.func, onOpenTech: PropTypes.func, selectedTechId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    height: PropTypes.string, showUnzonedOnly: PropTypes.bool,
};

export default memo(ZoneOperationsMap);
